/**
 * KDV settlement for W-29 Dönem özeti.
 *
 * VAT is declared month by month: a month first deducts the carry-over left by
 * the month before it, and whatever deduction is still unused carries into the
 * next month. So a multi-month view (çeyrek) has to walk the months in order and
 * add up the monthly payables — it is NOT max(0, Σ hesaplanan − Σ indirilecek),
 * which would silently net a deductible month against an earlier payable one.
 *
 * public.v_vat_summary groups by (period, tax_pct) where period is the month
 * start, so the same rate appears once per month; every helper here folds those
 * buckets instead of trusting the row count.
 */
import { D, sum, type Dec, type DecimalInput } from './money';

/** Earliest period we pull when we need the running carry-over, not just one period. */
export const VAT_HISTORY_START = '2000-01-01';

/** One (month, rate) bucket, shaped like a public.v_vat_summary row. */
export interface VatSummaryRow {
  period: string | null;
  tax_pct: DecimalInput;
  base_out: DecimalInput;
  vat_out: DecimalInput;
  base_in: DecimalInput;
  vat_in: DecimalInput;
}

export interface VatMonth {
  /** yyyy-mm-01 */
  period: string;
  /** hesaplanan KDV */
  out: Dec;
  /** indirilecek KDV */
  deductible: Dec;
  /** önceki dönemden devreden */
  carryIn: Dec;
  /** ödenecek KDV */
  payable: Dec;
  /** sonraki döneme devreden */
  carryOut: Dec;
}

/** Months in chronological order with the carry-over threaded through them. */
export function vatMonths(rows: VatSummaryRow[]): VatMonth[] {
  const byPeriod = new Map<string, { out: Dec; deductible: Dec }>();
  for (const r of rows) {
    if (!r.period) continue;
    const cur = byPeriod.get(r.period) ?? { out: D(0), deductible: D(0) };
    byPeriod.set(r.period, { out: cur.out.plus(D(r.vat_out)), deductible: cur.deductible.plus(D(r.vat_in)) });
  }
  const periods = [...byPeriod.keys()].sort();
  const out: VatMonth[] = [];
  let carry = D(0);
  for (const period of periods) {
    const m = byPeriod.get(period)!;
    // decimal.js isPositive() is true for zero, so compare explicitly
    const net = m.out.minus(m.deductible).minus(carry);
    const payable = net.greaterThan(0) ? net : D(0);
    const carryOut = net.lessThan(0) ? net.negated() : D(0);
    out.push({ period, out: m.out, deductible: m.deductible, carryIn: carry, payable, carryOut });
    carry = carryOut;
  }
  return out;
}

export interface VatSettlement {
  /** hesaplanan KDV inside the period */
  out: Dec;
  /** indirilecek KDV inside the period */
  deductible: Dec;
  /** devreden KDV coming in from before the period */
  carryIn: Dec;
  /** the month that carry came from (yyyy-mm-01), null when there is none */
  carryInPeriod: string | null;
  /** ödenecek KDV — the monthly payables added up */
  payable: Dec;
  /** devreden KDV going out to the next period */
  carryOut: Dec;
  /** the months that fall inside [from, to] */
  months: VatMonth[];
}

/**
 * Settle [from, to] against the carry-over built up by every earlier month in
 * `rows`. Pass the full history (from VAT_HISTORY_START), not just the period:
 * with only the period's own rows the carry-in is always zero.
 */
export function vatSettlement(rows: VatSummaryRow[], from: string, to: string): VatSettlement {
  const months = vatMonths(rows);
  const before = months.filter((m) => m.period < from);
  const inside = months.filter((m) => m.period >= from && m.period <= to);
  const last = before[before.length - 1];
  const carryIn = last?.carryOut ?? D(0);
  return {
    out: sum(inside.map((m) => m.out)),
    deductible: sum(inside.map((m) => m.deductible)),
    carryIn,
    carryInPeriod: last && last.carryOut.greaterThan(0) ? last.period : null,
    payable: sum(inside.map((m) => m.payable)),
    // a period with no VAT activity at all passes the carry straight through
    carryOut: inside[inside.length - 1]?.carryOut ?? carryIn,
    months: inside,
  };
}

export type VatDirection = 'out' | 'in';

export interface VatRateRow {
  taxPct: Dec;
  direction: VatDirection;
  /** matrah for this rate and direction */
  base: Dec;
  vat: Dec;
}

/**
 * Rate breakdown for [from, to], one row per rate AND direction — a sales matrah
 * and a purchase matrah are different numbers and must never be added together.
 * Folds the monthly buckets, so a quarter shows one row per rate, not three.
 */
export function vatRateRows(rows: VatSummaryRow[], from: string, to: string): VatRateRow[] {
  const acc = new Map<string, { taxPct: Dec; out: { base: Dec; vat: Dec }; in: { base: Dec; vat: Dec } }>();
  for (const r of rows) {
    if (!r.period || r.period < from || r.period > to) continue;
    const pct = D(r.tax_pct);
    const key = pct.toString();
    const cur = acc.get(key) ?? { taxPct: pct, out: { base: D(0), vat: D(0) }, in: { base: D(0), vat: D(0) } };
    cur.out = { base: cur.out.base.plus(D(r.base_out)), vat: cur.out.vat.plus(D(r.vat_out)) };
    cur.in = { base: cur.in.base.plus(D(r.base_in)), vat: cur.in.vat.plus(D(r.vat_in)) };
    acc.set(key, cur);
  }
  const out: VatRateRow[] = [];
  for (const v of acc.values()) {
    for (const direction of ['out', 'in'] as const) {
      const side = v[direction];
      if (side.base.isZero() && side.vat.isZero()) continue;
      out.push({ taxPct: v.taxPct, direction, base: side.base, vat: side.vat });
    }
  }
  // highest rate first, sales before purchases inside a rate
  return out.sort((a, b) => b.taxPct.comparedTo(a.taxPct) || (a.direction === b.direction ? 0 : a.direction === 'out' ? -1 : 1));
}

/** A tax_rates row, as much of it as the rate breakdown needs. */
export interface TaxRateLike {
  pct: DecimalInput;
  is_export_exempt?: boolean | null;
  is_active?: boolean | null;
}

/**
 * The percentages that are an export exemption (Amazon ihracatı), so the rate
 * table can say "(istisna)" instead of leaving a bare %0 row. v_vat_summary
 * carries the percentage and not the rate id, so a percentage only counts as
 * exempt when EVERY active rate at that percentage is one — a company that also
 * uses a plain %0 rate keeps an unlabelled row instead of a wrong label.
 */
export function exemptRatePcts(rates: TaxRateLike[]): Set<string> {
  const byPct = new Map<string, boolean>();
  for (const r of rates) {
    if (r.is_active === false) continue;
    const key = D(r.pct).toString();
    byPct.set(key, (byPct.get(key) ?? true) && !!r.is_export_exempt);
  }
  return new Set([...byPct].filter(([, exempt]) => exempt).map(([key]) => key));
}

/** KDV beyannamesi son günü: the 28th of the month after the period ends. */
export function vatDueDate(periodEnd: string): string {
  const [y, m] = periodEnd.split('-').map(Number) as [number, number];
  return m === 12 ? `${y + 1}-01-28` : `${y}-${String(m + 1).padStart(2, '0')}-28`;
}
